// Minutes stay on a continuous timeline. Never modulo individual point times:
// the explicit end boundary of a full-day profile must remain distinct.
export const LIGHT_PROTOCOL_VERSION = 1;
export const LIGHT_LIMITS = {
  channels: 72,
  points: 256,
  zoneChannels: 24,
  subzones: 4,
  localChannels: 6
};
export const channelKey = (c) => `${c.zone}:${c.subzone}:${c.channel}`;
export const channelLabel = (c) => c.name || `Subzone ${c.subzone + 1} / Channel ${c.channel + 1}`;
export const defaultLightProfile = () => [
  { offset_minutes: 0, level: 100, anchor: 0, interpolation: 1 },
  { offset_minutes: 0, level: 0, anchor: 1, interpolation: 1 }
];

export function timeMinutes(value) {
  if (typeof value === 'string') {
    const [hour, minute] = value.split(':').map(Number);
    return hour * 60 + minute;
  }
  return Number(value?.hour ?? 0) * 60 + Number(value?.minute ?? value?.min ?? 0);
}

export function lightCycle(timing = {}, phase = null) {
  const start = phase
    ? timeMinutes(phase.time_day_start)
    : Number(timing.start_day_hour ?? 0) * 60 + Number(timing.start_day_min ?? 0);
  let end = phase
    ? timeMinutes(phase.time_day_end)
    : Number(timing.end_day_hour ?? 0) * 60 + Number(timing.end_day_min ?? 0);
  if (end <= start) end += 1440;
  return { start, end };
}

export function resolvePoint(point, cycle) {
  return (Number(point.anchor) === 1 ? cycle.end : cycle.start) + Number(point.offset_minutes);
}

// A clock edit keeps the nearest occurrence on the current continuous timeline.
// Crossing midnight advances/retreats the day instead of jumping almost 24 hours.
export function clockOffsetForPoint(point, cycle, clockMinute) {
  const currentMinute = resolvePoint(point, cycle);
  const day = Math.floor((currentMinute - clockMinute + 720) / 1440);
  return clockMinute + day * 1440 - (point.anchor === 1 ? cycle.end : cycle.start);
}

export function sortedPoints(points, cycle) {
  return points
    .map((point, index) => ({ ...point, minute: resolvePoint(point, cycle), index }))
    .sort((a, b) => a.minute - b.minute);
}

// Prepare once when a caller samples several positions on the same curve.
export function prepareProfile(points, cycle) {
  if (!points?.length) return { points: [], error: null };
  if (points.length < 2)
    return { points: [], error: 'A custom profile needs at least two points.' };
  if (
    points.some(
      (p) =>
        !Number.isInteger(p.offset_minutes) ||
        Math.abs(p.offset_minutes) > 1440 ||
        !Number.isInteger(p.level) ||
        p.level < 0 ||
        p.level > 100 ||
        ![0, 1].includes(p.anchor) ||
        ![0, 1].includes(p.interpolation)
    )
  )
    return {
      points: [],
      error: 'Points need whole-minute offsets from −1440 to 1440, levels from 0 to 100%, and a valid anchor and interpolation.'
    };
  const resolved = sortedPoints(points, cycle);
  if (resolved.some((p, i) => i > 0 && p.minute === resolved[i - 1].minute))
    return { points: [], error: 'Two points resolve to the same minute.' };
  if (resolved[resolved.length - 1].minute - resolved[0].minute > 1440)
    return { points: [], error: 'The profile spans more than 24 hours.' };
  return { points: resolved, error: null };
}

export function profileError(points, cycle) {
  return prepareProfile(points, cycle).error;
}

// Unknown settings must remain raw readback, not acquire meaning through a
// checkbox, slider or preview. Timing-invalid retained curves are still editable.
export function lightPhaseValuesKnown(lighting, { allowRangeErrors = false } = {}) {
  if (!lighting || lighting.schema_version === 0) return true;
  const inRange = (value, min, max) =>
    allowRangeErrors
      ? typeof value === 'number' && Number.isFinite(value)
      : Number.isInteger(value) && value >= min && value <= max;
  return (
    lighting.schema_version === 1 &&
    [0, 1, 2, 3].includes(lighting.mode) &&
    Number.isInteger(lighting.master_override_mask) &&
    lighting.master_override_mask >= 0 &&
    lighting.master_override_mask <= 31 &&
    [lighting.enabled_override_mask, lighting.enabled_mask].every(
      (mask) => Number.isInteger(mask) && mask >= 0 && mask <= 31
    ) &&
    inRange(lighting.zone_master, 0, 100) &&
    lighting.master?.length === 4 &&
    lighting.master.every((value) => inRange(value, 0, 100)) &&
    Array.isArray(lighting.channels) &&
    lighting.channels.every(
      (channel) =>
        [0, 1, 2].includes(channel.kind) &&
        typeof channel.maximum_override === 'boolean' &&
        typeof channel.enable_override === 'boolean' &&
        typeof channel.enabled === 'boolean' &&
        inRange(channel.maximum, 0, 115) &&
        Array.isArray(channel.points) &&
        channel.points.every(
          (point) =>
            inRange(point.offset_minutes, -1440, 1440) &&
            inRange(point.level, 0, 100) &&
            [0, 1].includes(point.anchor) &&
            [0, 1].includes(point.interpolation)
        )
    )
  );
}

// Only an explicit editor action may snap a point. Time changes never rewrite it.
export function editProfilePoint(points, index, change, cycle, { allowPartialRepair = true } = {}) {
  const edited = { ...points[index], ...change };
  edited.offset_minutes = Math.round(Number(edited.offset_minutes));
  edited.level = Math.max(0, Math.min(100, Math.round(Number(edited.level))));
  const other = points.filter((_, i) => i !== index);
  const occupied = new Set(other.map((p) => resolvePoint(p, cycle)));
  const anchor = edited.anchor === 1 ? cycle.end : cycle.start;
  const wanted = resolvePoint(edited, cycle);
  const originalError = profileError(points, cycle);
  const repairingTiming =
    allowPartialRepair &&
    isLightTimingError(originalError) &&
    isLightTimingError(profileError(other, cycle));
  const span = (profile) => {
    const minutes = profile.map((point) => resolvePoint(point, cycle));
    return Math.max(...minutes) - Math.min(...minutes);
  };
  const allowedSpan = Math.max(1440, span(points));
  for (let distance = 0; distance <= 2880; distance++) {
    for (const minute of distance === 0 ? [wanted] : [wanted + distance, wanted - distance]) {
      if (occupied.has(minute) || Math.abs(minute - anchor) > 1440) continue;
      const candidate = { ...edited, offset_minutes: minute - anchor };
      const next = [...other, candidate];
      const error = profileError(next, cycle);
      // A Time edit may invalidate several unrelated pairs at once. Permit
      // repairing them one at a time, without introducing a new collision or
      // making an existing overlong span worse. Save still validates the whole
      // profile; no point is silently changed just because Time changed.
      if (error && (!repairingTiming || !isLightTimingError(error) || span(next) > allowedSpan))
        continue;
      return {
        points: next.sort((a, b) => resolvePoint(a, cycle) - resolvePoint(b, cycle)),
        point: candidate,
        snapped: distance !== 0,
        minute
      };
    }
  }
  return { points, error: 'No free minute fits this profile. Your previous points were retained.' };
}

export const copyLightProfilePoints = (points) =>
  points.map(({ anchor, offset_minutes, level, interpolation }) => ({
    anchor,
    offset_minutes,
    level,
    interpolation
  }));

// Extend the selected anchor's group, then let the usual point editor snap
// into the nearest valid free minute when bounds or another point intervene.
export function nextProfilePoint(points, selected) {
  const anchor = selected?.anchor === 1 ? 1 : 0;
  const group = points.filter((point) => point.anchor === anchor);
  const offsets = (group.length ? group : [selected ?? { offset_minutes: 0 }]).map(
    (point) => point.offset_minutes
  );
  return {
    anchor,
    offset_minutes: anchor === 1 ? Math.min(...offsets) - 30 : Math.max(...offsets) + 30,
    level: selected?.level ?? 100,
    interpolation: selected?.interpolation ?? 0
  };
}

export function evaluateProfile(points, cycle, minute) {
  return evaluateResolvedProfile(prepareProfile(points, cycle).points, minute);
}

// Input is the resolved points returned by prepareProfile, including [] for an
// invalid/empty curve. Evaluation never changes the caller's prepared points.
export function evaluateResolvedProfile(sorted, minute) {
  if (!sorted.length) return 0;
  const first = sorted[0].minute;
  const now = first + ((((minute - first) % 1440) + 1440) % 1440);
  if (now >= sorted[sorted.length - 1].minute) return 0;
  for (let i = 1; i < sorted.length; i++) {
    if (now < sorted[i].minute) {
      const a = sorted[i - 1],
        b = sorted[i];
      return a.interpolation === 1
        ? a.level
        : a.level + ((b.level - a.level) * (now - a.minute)) / (b.minute - a.minute);
    }
  }
  return 0;
}

export function formatLightMinute(minute) {
  const day = Math.floor(minute / 1440);
  const clock = ((minute % 1440) + 1440) % 1440;
  return `${String(Math.floor(clock / 60)).padStart(2, '0')}:${String(clock % 60).padStart(2, '0')}${day ? ` (${day > 0 ? '+' : ''}${day}d)` : ''}`;
}

export function supportsLightChannelVersion(controller) {
  const api = controller?.api_version ?? controller?.settings?.system?.api_version ?? '';
  return [5155, 6000].includes(Number(controller?.type)) && String(api).localeCompare('BAID') >= 0;
}

export function hasLightChannelCapability(controller) {
  const info = controller?.settings?.light_channels_info;
  return (
    supportsLightChannelVersion(controller) &&
    (Number(info?.capabilities) & 3) === 3 &&
    Number(info?.schema_version) === LIGHT_PROTOCOL_VERSION
  );
}

export function supportsLightChannels(controller) {
  return (
    hasLightChannelCapability(controller) &&
    Number(controller?.settings?.light_channels?.schema_version) === LIGHT_PROTOCOL_VERSION
  );
}

export function supportsScheduledLightChannels(controller, protocolVersion) {
  return (
    protocolVersion === 7 &&
    supportsLightChannels(controller) &&
    Boolean(controller.settings.light_channels_info.capabilities & 4)
  );
}

export const lightStorageAvailable = (info) => [0, 1].includes(info?.storage_state);

export function lightLayoutKnown(info) {
  return Object.entries({
    channel_capacity: 72,
    point_capacity: 256,
    zone_capacity: 9,
    subzones_per_zone: 4,
    channels_per_subzone: 6
  }).every(([key, expected]) => info?.[key] == null || info[key] === expected);
}

export function lightStatusUnknown(info) {
  return Boolean(
    info &&
      (![0, 1, 2].includes(info.storage_state) ||
        !lightLayoutKnown(info) ||
        info.capabilities & ~15 ||
        [
          'advanced_zone_mask',
          'effective_advanced_zone_mask',
          'fallback_zone_mask',
          'phase_zone_mask'
        ].some((key) => info[key] & ~511) ||
        info.zone_faults?.some((fault) => fault & ~15) ||
        info.channels?.some((channel) => channel.flags & ~63))
  );
}

export const isLightTimingError = (error) =>
  error === 'Two points resolve to the same minute.' ||
  error === 'The profile spans more than 24 hours.';
export function sameLightPoints(a = [], b = []) {
  const canonical = (points) =>
    points.map((p) => [p.anchor, p.offset_minutes, p.level, p.interpolation].join(':')).sort();
  return JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));
}

export function lightCatalogErrors(catalog, cycles = {}, baseline = null) {
  const errors = [];
  if (
    !Number.isInteger(catalog?.advanced_zone_mask) ||
    catalog.advanced_zone_mask < 0 ||
    catalog.advanced_zone_mask > 511
  )
    errors.push('Unsupported advanced lighting zone mask.');
  const channels = catalog?.channels ?? [];
  if (channels.length > LIGHT_LIMITS.channels)
    errors.push('The global channel pool is full (72 channels).');
  if (channels.reduce((n, c) => n + (c.points?.length ?? 0), 0) > LIGHT_LIMITS.points)
    errors.push('The global point pool is full (256 points).');
  const seen = new Set();
  if (catalog?.zones?.length !== 9)
    errors.push('The complete zone catalog is required. Refresh the controller settings.');
  const zones = new Set();
  for (const z of catalog?.zones ?? []) {
    if (!Number.isInteger(z.zone) || z.zone < 0 || z.zone > 8 || zones.has(z.zone))
      errors.push('Invalid or duplicate lighting zone.');
    zones.add(z.zone);
    if (
      !Number.isInteger(z.master) ||
      z.master < 0 ||
      z.master > 100 ||
      ![0, 1].includes(z.enabled) ||
      ![0, 1].includes(z.shared_toplight)
    )
      errors.push('Invalid zone master or lighting options.');
    if (
      z.shared_toplight === 1 &&
      !channels.some((c) => c.zone === z.zone && c.subzone === 0 && c.channel === 0)
    )
      errors.push(`Zone ${z.zone + 1}: the shared top-light channel is not allocated.`);
  }
  if (catalog?.subzones?.length !== 36)
    errors.push('The complete subzone catalog is required. Refresh the controller settings.');
  const subzones = new Set();
  for (const s of catalog?.subzones ?? []) {
    const key = `${s.zone}:${s.subzone}`;
    if (
      subzones.has(key) ||
      !Number.isInteger(s.zone) ||
      s.zone < 0 ||
      s.zone > 8 ||
      !Number.isInteger(s.subzone) ||
      s.subzone < 0 ||
      s.subzone > 3
    )
      errors.push('Invalid or duplicate subzone.');
    subzones.add(key);
    if (
      !Number.isInteger(s.master) ||
      s.master < 0 ||
      s.master > 100 ||
      ![0, 1].includes(s.enabled) ||
      !Number.isInteger(s.enabled_mask) ||
      s.enabled_mask < 0 ||
      s.enabled_mask > 63 ||
      !Number.isInteger(s.visible_mask) ||
      s.visible_mask < 0 ||
      s.visible_mask > 63
    )
      errors.push('Invalid subzone level or channel mask.');
    if (
      s.main_channel !== 255 &&
      !channels.some(
        (c) => c.zone === s.zone && c.subzone === s.subzone && c.channel === s.main_channel
      )
    )
      errors.push(
        `Zone ${s.zone + 1}, subzone ${s.subzone + 1}: the main channel is not allocated.`
      );
    const allocated = channels
      .filter((c) => c.zone === s.zone && c.subzone === s.subzone)
      .reduce((mask, c) => mask | (1 << c.channel), 0);
    if ((s.enabled_mask | s.visible_mask) & ~allocated)
      errors.push('Channel masks reference an unallocated channel.');
  }
  for (const c of channels) {
    if (
      ![c.zone, c.subzone, c.channel].every(Number.isInteger) ||
      c.zone < 0 ||
      c.zone > 8 ||
      c.subzone < 0 ||
      c.subzone > 3 ||
      c.channel < 0 ||
      c.channel > 5
    )
      errors.push('Invalid channel identity.');
    if (seen.has(channelKey(c))) errors.push('Duplicate channel identity.');
    seen.add(channelKey(c));
    if (
      !Number.isInteger(c.minimum) ||
      !Number.isInteger(c.maximum) ||
      c.minimum < 0 ||
      c.maximum > 115 ||
      c.minimum > c.maximum
    )
      errors.push(`${channelLabel(c)}: minimum and maximum must be between 0 and 115%.`);
    if (new Blob([c.name ?? '']).size > 20)
      errors.push(`${channelLabel(c)}: names are limited to 20 UTF-8 bytes.`);
    const error = profileError(c.points, cycles[c.zone] ?? { start: 0, end: 720 });
    const previous = baseline?.channels?.find((p) => channelKey(p) === channelKey(c));
    const activatesAdvanced =
      Boolean(catalog.advanced_zone_mask & (1 << c.zone)) &&
      !Boolean(baseline?.advanced_zone_mask & (1 << c.zone));
    const retainsInvalid =
      previous && sameLightPoints(previous.points, c.points) && !activatesAdvanced;
    if (error && (!isLightTimingError(error) || (cycles[c.zone] && !retainsInvalid)))
      errors.push(`${channelLabel(c)}: ${error}`);
  }
  return errors;
}

export function missingPhaseLightChannels(lighting, installations = []) {
  return (lighting?.channels ?? []).filter(
    (channel) =>
      !installations.some(
        (installed) =>
          installed.subzone === channel.subzone && installed.channel === channel.channel
      )
  );
}

export function phaseLightErrors(lighting, cycle, installations = [], baseline = null) {
  if (!lighting || lighting.schema_version === 0) return [];
  const errors = [];
  if (lighting.schema_version !== 1 || ![0, 1, 2, 3].includes(lighting.mode))
    errors.push('Unsupported phase lighting mode.');
  if (
    !Number.isInteger(lighting.master_override_mask) ||
    lighting.master_override_mask < 0 ||
    lighting.master_override_mask > 31 ||
    !Number.isInteger(lighting.zone_master) ||
    lighting.zone_master < 0 ||
    lighting.zone_master > 100 ||
    lighting.master?.length !== 4 ||
    lighting.master.some((m) => !Number.isInteger(m) || m < 0 || m > 100)
  )
    errors.push('Invalid phase master levels.');
  if (
    [lighting.enabled_override_mask, lighting.enabled_mask].some(
      (mask) => !Number.isInteger(mask) || mask < 0 || mask > 31
    )
  )
    errors.push('Invalid phase enable selection.');
  if (
    lighting.channels.length > 24 ||
    lighting.channels.reduce((n, c) => n + c.points.length, 0) > 256
  )
    errors.push('Phase lighting exceeds its channel or point capacity.');
  const keys = new Set();
  for (const c of lighting.channels) {
    const key = `${c.subzone}:${c.channel}`;
    const installation = installations.find(
      (i) => i.subzone === c.subzone && i.channel === c.channel
    );
    if (keys.has(key) || !installation)
      errors.push('Phase channel is duplicated or not installed.');
    keys.add(key);
    if (![0, 1, 2].includes(c.kind)) errors.push('Invalid phase channel behavior.');
    if (c.maximum_override && (!Number.isInteger(c.maximum) || c.maximum < 0 || c.maximum > 115))
      errors.push('A phase maximum must be between 0 and 115%.');
    if (c.kind === 1) {
      const error =
        c.points.length < 2
          ? 'A custom phase profile needs at least two points.'
          : profileError(c.points, cycle);
      const previous = baseline?.channels?.find(
        (p) => p.subzone === c.subzone && p.channel === c.channel
      );
      const retainsInvalid =
        previous?.kind === 1 &&
        sameLightPoints(previous.points, c.points) &&
        !(lighting.mode === 2 && baseline.mode !== 2);
      if (error && (!isLightTimingError(error) || !retainsInvalid)) errors.push(error);
    } else if (c.points.length) errors.push('Only custom phase profiles own points.');
  }
  return errors;
}

export function scheduleLightWarnings(catalog, zone, schedule, timing) {
  return (schedule?.schedule_values?.scheduling_values ?? []).flatMap((phase, index) => {
    if (phase._deleted) return [];
    const cycle = lightCycle(
      timing,
      schedule.schedule_meta?.schedule_day_night_enabled ? phase : null
    );
    const lighting = schedule.schedule_meta?.schedule_lights_enabled ? phase.lighting : null;
    const installations = catalog?.channels?.filter((c) => c.zone === zone) ?? [];
    // Warn about retained profiles even in Simple/Off mode: a Time edit must
    // not silently break the configuration the user intends to restore later.
    const inherited = invalidLightProfiles(catalog, zone, cycle).filter(
      (c) =>
        !(lighting?.channels ?? []).some(
          (p) => `${zone}:${p.subzone}:${p.channel}` === c.channel && p.kind !== 0
        )
    );
    const custom = (lighting?.channels ?? [])
      .filter((c) => c.kind === 1)
      .map((c) => ({
        zone,
        channel: `${zone}:${c.subzone}:${c.channel}`,
        label: channelLabel(
          installations.find((i) => i.subzone === c.subzone && i.channel === c.channel) || c
        ),
        error: profileError(c.points, cycle)
      }))
      .filter((c) => c.error);
    return [...inherited, ...custom].map((c) => ({
      ...c,
      phase: phase.name_phase_short || `Phase ${index + 1}`
    }));
  });
}

export function invalidLightProfiles(catalog, zone, cycle) {
  return (catalog?.channels ?? [])
    .filter((c) => c.zone === Number(zone) && c.points?.length)
    .map((c) => ({
      zone,
      channel: channelKey(c),
      label: channelLabel(c),
      error: profileError(c.points, cycle)
    }))
    .filter((c) => c.error);
}

export function lightSwitchLabel(index, channels = [], zone = 0) {
  if (index === 0) return 'enable_day_night';
  if (index >= 1 && index <= 4) return `Subzone ${index} light power`;
  if (index === 5) return 'Any light channel: shared power';
  const subzone = Math.floor((index - 8) / 8),
    channel = (index - 8) % 8;
  if (subzone >= 0 && subzone < 4 && channel >= 0 && channel < 6) {
    const configured = channels.find(
      (c) => c.zone === Number(zone) && c.subzone === subzone && c.channel === channel
    );
    return `Subzone ${subzone + 1} → ${configured?.name || `Channel ${channel + 1}`}`;
  }
  return 'Reserved';
}
